import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Layout from "../../components/Layout";
import WizardSteps from "../../components/WizardSteps";
import Loading from "../../components/Loading";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import { fetchPropertyDraft } from "../../services/propertyDraft";

import FormOption from "./Enum";
import MapForm from "./components/MapForm";
import DescriptionForm from "./components/DescriptionForm";
import PhotosForm from "./components/PhotosForm";
import type { PropertyChildInput } from "../../types/propertyChildren";

const STEP_ITEMS = [
  { id: String(FormOption.Map), label: "ENDEREÇO" },
  { id: String(FormOption.Description), label: "IMÓVEL" },
  { id: String(FormOption.Publication), label: "PUBLICAÇÃO" },
  { id: String(FormOption.Characteristic), label: "CARACTERÍSTICAS" },
  { id: String(FormOption.Photos), label: "FOTOS" },
];

interface DraftPhoto {
  id: number;
  url_l: string;
  url_m: string;
  url_s: string;
  order?: number;
}

function mapDraftPhotos(raw: unknown): DraftPhoto[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((photo) => {
      const item = photo as Record<string, unknown>;
      const url =
        String(item.url_m ?? item.url_l ?? item.photo ?? item.url_s ?? "");
      return {
        id: Number(item.id),
        url_l: url,
        url_m: url,
        url_s: url,
        order: item.order != null ? Number(item.order) : undefined,
      };
    })
    .filter((photo) => Number.isFinite(photo.id));
}

const PropertyCreate: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { access_token, dateToExpires, user } = useAuth();
  const { addToast } = useToast();

  const draftIdParam = useMemo(
    () => new URLSearchParams(location.search).get("draftId"),
    [location.search]
  );

  const [formOptions, setFormOptions] = useState({
    current: FormOption.Map,
    further: FormOption.Map,
  });

  const [addressID, setAddressID] = useState<number>();
  const [propertyID, setPropertyID] = useState<number>();
  const [draftProperty, setDraftProperty] = useState<Record<string, unknown>>();
  const [initialPhotos, setInitialPhotos] = useState<DraftPhoto[]>([]);
  const [canPublish, setCanPublish] = useState(false);
  const [publishBlockers, setPublishBlockers] = useState<string[]>([]);
  const [loadingDraft, setLoadingDraft] = useState(Boolean(draftIdParam));
  const [localChildren, setLocalChildren] = useState<PropertyChildInput[]>([]);

  useEffect(() => {
    if (!draftIdParam || !user) {
      setLoadingDraft(false);
      return;
    }

    const draftId = Number(draftIdParam);
    if (!Number.isFinite(draftId)) {
      setLoadingDraft(false);
      return;
    }

    async function loadDraft() {
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const result = await fetchPropertyDraft(api, draftId);
        const property = result.property;

        setPropertyID(draftId);
        setAddressID(
          property.address_id != null ? Number(property.address_id) : undefined
        );
        setDraftProperty(property);
        setInitialPhotos(mapDraftPhotos(property.photos));
        setCanPublish(result.canPublish);
        setPublishBlockers(result.publishBlockers);
        setFormOptions({
          current: FormOption.Description,
          further: FormOption.Photos,
        });
      } catch (err: any) {
        addToast({
          title: err?.response?.data?.message ?? "Erro ao carregar rascunho",
          type: "error",
        });
        history.replace("/create-property");
      } finally {
        setLoadingDraft(false);
      }
    }

    loadDraft();
  }, [
    draftIdParam,
    user,
    access_token,
    dateToExpires,
    addToast,
    history,
  ]);

  function handleSetFormOption(formToGo: number) {
    setFormOptions((curFormOption) => {
      const further =
        formToGo > curFormOption.further ? formToGo : curFormOption.further;
      return { ...curFormOption, current: formToGo, further };
    });
  }

  function isFormFree(targetForm: number) {
    return targetForm <= formOptions.further;
  }

  function handleChangeFormOnClick(targetForm: number) {
    if (isFormFree(targetForm))
      setFormOptions((curFormOptions) => ({
        ...curFormOptions,
        current: targetForm,
      }));
  }

  function handlePublishMetaChange(meta: {
    canPublish: boolean;
    publishBlockers: string[];
  }) {
    setCanPublish(meta.canPublish);
    setPublishBlockers(meta.publishBlockers);
  }

  if (loadingDraft) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <div>
      <Layout>
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center">
          <div className="mt-2 w-full px-3 py-4 md:mt-6 md:px-6 md:py-6">
            <div className="flex items-center justify-start border-slate-200/80 md:border-b md:pb-6">
              <div className="flex w-full flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 md:text-3xl">
                    Cadastrar imóvel
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Preencha cada etapa para publicar o imóvel.
                  </p>
                </div>
              </div>
            </div>

            <WizardSteps
              items={STEP_ITEMS}
              activeId={String(formOptions.current)}
              isUnlocked={(id) => isFormFree(Number(id))}
              onSelect={(id) => handleChangeFormOnClick(Number(id))}
            />

            <div
              className={
                formOptions.current === FormOption.Map ? undefined : "hidden"
              }
            >
              <MapForm
                addressID={addressID}
                propertyID={propertyID}
                localChildren={localChildren}
                onLocalChildrenChange={setLocalChildren}
                onChangeCurrentForm={() =>
                  handleSetFormOption(FormOption.Description)
                }
                onAddressIdReceived={(id) => setAddressID(id)}
              />
            </div>

            {(formOptions.current === FormOption.Description ||
              formOptions.current === FormOption.Publication ||
              formOptions.current === FormOption.Characteristic) && (
              <DescriptionForm
                phase={
                  formOptions.current === FormOption.Publication
                    ? "publication"
                    : formOptions.current === FormOption.Characteristic
                    ? "characteristics"
                    : "property"
                }
                address_id={addressID}
                propertyID={propertyID}
                initialProperty={draftProperty}
                localChildren={localChildren}
                onLocalChildrenChange={setLocalChildren}
                onGoToProperty={() =>
                  handleSetFormOption(FormOption.Description)
                }
                onGoToPublication={() =>
                  handleSetFormOption(FormOption.Publication)
                }
                onGoToPhotos={() => handleSetFormOption(FormOption.Photos)}
                onChangeCurrentForm={() =>
                  handleSetFormOption(FormOption.Characteristic)
                }
                onPropertyIdReceived={(id) => setPropertyID(id)}
              />
            )}

            {formOptions.current === FormOption.Photos && (
              <PhotosForm
                propertyID={propertyID}
                canPublish={canPublish}
                publishBlockers={publishBlockers}
                initialPhotos={initialPhotos}
                onPublishMetaChange={handlePublishMetaChange}
              />
            )}
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default PropertyCreate;
